import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Button,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import AppConfig from "appConfig";
import usePageTitle from "app/pageTitle";

import MapPicker from "react-google-map-picker";
import GoogleAddressField from "app/components/google_address";
import DataTable from "theme/Tables/DataTable";

function AttendanceViewPage() {
  const title = "Attendance Tracker";
  usePageTitle(title);

  const { ref } = useParams();
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const [locations, setLocations] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [groups, setGroups] = useState([]);
  const [agentGroups, setAgentGroups] = useState([]);

  const [attendanceTitle, setAttendanceTitle] = useState(null);
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [gracePeriod, setGracePeriod] = useState(null);
  const [enforceFaceMatch, setEnforceFaceMatch] = useState(null);

  const [locationTitle, setLocationTitle] = useState(null);
  const [locationLat, setLocationLat] = useState(null);
  const [locationLng, setLocationLng] = useState(null);
  const [locationRad, setLocationRad] = useState(100);
  const [centerLocation, setCenterLocation] = useState(null);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [openEditLocationDialog, setOpenEditLocationDialog] = useState(false);

  const [customFieldTitle, setCustomFieldTitle] = useState(null);
  const [customFieldOptions, setCustomFieldOptions] = useState(null);
  const [selectedCustomField, setSelectedCustomField] = useState(null);
  const [openNewFieldDialog, setOpenNewFieldDialog] = useState(false);
  const [openEditFieldDialog, setOpenEditFieldDialog] = useState(false);

  const [selectedAgentGroup, setSelectedAgentGroup] = useState(null);
  const [openNewAgentDialog, setOpenNewAgentDialog] = useState(false);

  const [aggregators, setAggregators] = useState([]);
  const [accountAggregators, setAccountAggregators] = useState([]);
  const [selectedAccountAggregator, setSelectedAccountAggregator] = useState(null);
  const [openNewAggregatorDialog, setOpenNewAggregatorDialog] = useState(false);

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [openPackagesDialog, setOpenPackagesDialog] = useState(false);

  const [percentageUsed, setPercentageUsed] = useState(0);

  const getProgressColor = () => {
    if (percentageUsed > 50) return "success";
    if (percentageUsed > 20) return "warning";
    return "error";
  };

  function generateTimeArray() {
    const startTime = new Date();
    const endTime = new Date();
    startTime.setHours(0, 0, 0, 0);
    endTime.setHours(23, 59, 0, 0);

    const timeArray = [];
    const currentTime = new Date(startTime);

    while (currentTime <= endTime) {
      const hours = String(currentTime.getHours()).padStart(2, "0");
      const minutes = String(currentTime.getMinutes()).padStart(2, "0");
      timeArray.push(`${hours}:${minutes}`);
      currentTime.setMinutes(currentTime.getMinutes() + 30);
    }

    return timeArray;
  }

  const timeArray = generateTimeArray();

  async function deleteLocation(locationRef) {
    setShowLoading(true);
    await axios
      .get(`/portal/attendances/locations/${locationRef}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function updateLocationStatus(locationRef) {
    setShowLoading(true);
    await axios
      .get(`/portal/attendances/locations/${locationRef}/status.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const locationsTableColumns = [
    { Header: "Name", accessor: "title", align: "left" },
    { Header: "Latitude", accessor: "latitude", align: "left" },
    { Header: "Longitude", accessor: "longitude", align: "left" },
    { Header: "Coverage Radius", accessor: "radius", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildLocationsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        title: row.title,
        latitude: row.latitude,
        longitude: row.longitude,
        radius: `${row.coverage_radius}m`,
        status: (
          <MDBox>
            <Icon color={row.status ? "success" : "error"}>
              {row.status ? "check_circle" : "cancel"}
            </Icon>
          </MDBox>
        ),
        actions: (
          <MDBox sx={{ width: "100%" }}>
            <MDBox mt={1} display="flex" justifyContent="space-evenly" alignItems="start">
              <Tooltip title="View On Map">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="error"
                  iconOnly
                  onClick={() => {
                    const maplink = `https://maps.google.com?q=${row.latitude},${row.longitude}`;
                    window.open(maplink, "_blank");
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    location_on
                  </Icon>
                </MDButton>
              </Tooltip>
              &nbsp;
              <Tooltip title="Edit">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="warning"
                  iconOnly
                  onClick={() => {
                    setSelectedLocation(row);
                    setLocationTitle(row.title);
                    setLocationLat(row.latitude);
                    setLocationLng(row.longitude);
                    setLocationRad(row.coverage_radius);
                    setOpenEditLocationDialog(true);
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    edit
                  </Icon>
                </MDButton>
              </Tooltip>
              &nbsp;
              <Tooltip title={`${row.status ? "Disable" : "Enable"} Location?`}>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  iconOnly
                  onClick={() => {
                    if (
                      window.confirm(`${row.status ? "Disable" : "Enable"} this location?`) === true
                    ) {
                      updateLocationStatus(row.reference);
                    }
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    {row.status ? "pause" : "play_arrow"}
                  </Icon>
                </MDButton>
              </Tooltip>
              &nbsp;
              <Tooltip title="Delete Location?">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="error"
                  iconOnly
                  onClick={() => {
                    if (window.confirm(`Delete location?`) === true) {
                      deleteLocation(row.reference);
                    }
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    delete
                  </Icon>
                </MDButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  async function deleteCustomField(reference) {
    setShowLoading(true);
    await axios
      .get(`portal/attendances/custom_fields/${reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const customFieldTableColumns = [
    { Header: "Field", accessor: "field", align: "left" },
    { Header: "Actions", accessor: "action", align: "right" },
  ];

  const buildCustomFieldRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        field: row.title,
        action: (
          <MDBox mt={1} variant="button">
            <Tooltip title="Edit">
              <MDButton
                size="small"
                variant="outlined"
                color="warning"
                iconOnly
                onClick={() => {
                  setSelectedCustomField(row);
                  setCustomFieldTitle(row.title);
                  setCustomFieldOptions(row.options.join(","));
                  setOpenEditFieldDialog(true);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                  edit_note
                </Icon>
              </MDButton>
            </Tooltip>
            &nbsp;
            <Tooltip title="Delete">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                onClick={() => {
                  if (window.confirm(`Remove ${row.title}?`) === true) {
                    deleteCustomField(row.reference);
                  }
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                  delete
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  async function deleteGroup(reference) {
    setShowLoading(true);
    await axios
      .get(`portal/attendances/agent_groups/${reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const groupsTableColumns = [
    { Header: "Group", accessor: "group", align: "left" },
    { Header: "Actions", accessor: "action", align: "right" },
  ];

  const buildGroupsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        group: row.title,
        action: (
          <MDBox mt={1} variant="button">
            <Tooltip title="Delete">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                onClick={() => {
                  if (window.confirm(`Remove ${row.title}?`) === true) {
                    deleteGroup(row.reference);
                  }
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                  delete
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  async function deleteAggregator(reference) {
    setShowLoading(true);
    await axios
      .get(`portal/attendances/aggregators/${reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const aggregatorsTableColumns = [
    { Header: "Aggregator", accessor: "aggregator", align: "left" },
    { Header: "Actions", accessor: "action", align: "right" },
  ];

  const buildAggregatorsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        aggregator: row.title,
        action: (
          <MDBox mt={1} variant="button">
            <Tooltip title="Delete">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                onClick={() => {
                  if (window.confirm(`Remove ${row.title}?`) === true) {
                    deleteAggregator(row.reference);
                  }
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                  delete
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/attendances/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);
          // console.log(result.data.data);
          if (result.data.status === "success") {
            setAttendance(result.data.data.attendance);
            const percentRemaining =
              ((result.data.data.attendance.quota - result.data.data.attendance.usedQuota) /
                result.data.data.attendance.quota) *
              100;
            setPercentageUsed(percentRemaining);

            setPackages(result.data.data.packages);
            setAttendanceTitle(result.data.data.attendance.title);
            setCheckInTime(result.data.data.attendance.check_in_time);
            setCheckOutTime(result.data.data.attendance.check_out_time);
            setGracePeriod(result.data.data.attendance.grace_period);
            setEnforceFaceMatch(result.data.data.attendance.enforce_face_check);
            setAgentGroups(result.data.data.agent_groups);
            setAccountAggregators(result.data.data.account_aggregators);

            const locationRows = buildLocationsRows(result.data.data.locations);
            setLocations(locationRows);

            const rowsF = buildCustomFieldRows(result.data.data.custom_fields);
            setCustomFields(rowsF);

            const rowsG = buildGroupsRows(result.data.data.groups);
            setGroups(rowsG);

            const rowsA = buildAggregatorsRows(result.data.data.aggregators);
            setAggregators(rowsA);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function editAttendance() {
    if (title === null) {
      setSeverity("error");
      setErrMsg("Attendance name is required.");
      setOpen(true);
      return;
    }

    if (checkInTime === null) {
      setSeverity("error");
      setErrMsg("Check-in time is required.");
      setOpen(true);
      return;
    }

    setShowLoading(true);
    const json = {
      reference: ref,
      title: attendanceTitle,
      check_in_time: checkInTime,
      check_out_time: checkOutTime,
      grace_period: gracePeriod,
      enforce_face_check: enforceFaceMatch,
    };

    await axios
      .post("portal/attendances/edit.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  function onLocationPicked(clat, clng) {
    setLocationLat(clat);
    setLocationLng(clng);
  }

  useEffect(() => {
    if (locationLat === null) {
      return;
    }

    if (locationLng === null) {
      return;
    }

    setCenterLocation({
      lat: locationLat,
      lng: locationLng,
    });
  }, [locationLat, locationLng]);

  async function createLocation() {
    if (locationTitle === null) {
      setSeverity("error");
      setErrMsg("A location name is required.");
      setOpen(true);
      return;
    }

    if (locationLat === null) {
      setSeverity("error");
      setErrMsg("Location latitude is required");
      setOpen(true);
      return;
    }

    if (locationLng === null) {
      setSeverity("error");
      setErrMsg("Location longitude is required");
      setOpen(true);
      return;
    }

    if (locationRad === null) {
      setSeverity("error");
      setErrMsg("Location area radius is required");
      setOpen(true);
      return;
    }

    if (parseInt(locationRad, 10) < 10) {
      setSeverity("error");
      setErrMsg("Location area radius is too restricted. A minimum of 10meters is required.");
      setOpen(true);
      return;
    }

    setOpenLocationDialog(false);
    setShowLoading(true);
    const json = {
      attendance: ref,
      title: locationTitle,
      latitude: locationLat,
      longitude: locationLng,
      radius: parseInt(locationRad, 10),
    };

    await axios
      .post("portal/attendances/locations/create.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function editLocation() {
    if (locationTitle === null) {
      setSeverity("error");
      setErrMsg("A location name is required.");
      setOpen(true);
      return;
    }

    if (locationLat === null) {
      setSeverity("error");
      setErrMsg("Location latitude is required");
      setOpen(true);
      return;
    }

    if (locationLng === null) {
      setSeverity("error");
      setErrMsg("Location longitude is required");
      setOpen(true);
      return;
    }

    if (locationRad === null) {
      setSeverity("error");
      setErrMsg("Location area radius is required");
      setOpen(true);
      return;
    }

    if (parseInt(locationRad, 10) < 10) {
      setSeverity("error");
      setErrMsg("Location area radius is too restricted. A minimum of 10meters is required.");
      setOpen(true);
      return;
    }

    setOpenEditLocationDialog(false);
    setShowLoading(true);
    const json = {
      reference: selectedLocation.reference,
      title: locationTitle,
      latitude: locationLat,
      longitude: locationLng,
      radius: parseInt(locationRad, 10),
    };

    await axios
      .post("portal/attendances/locations/edit.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function createCustomField() {
    if (customFieldTitle === null) {
      setSeverity("error");
      setErrMsg("Field name is required.");
      setOpen(true);
      return;
    }

    setOpenNewFieldDialog(false);
    setShowLoading(true);
    const json = {
      name: customFieldTitle,
      options: customFieldOptions,
      attendance: ref,
    };

    await axios
      .post("portal/attendances/custom_fields/create.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function editCustomField() {
    if (customFieldTitle === null) {
      setSeverity("error");
      setErrMsg("Field name is required.");
      setOpen(true);
      return;
    }

    setOpenEditFieldDialog(false);
    setShowLoading(true);
    const json = {
      name: customFieldTitle,
      options: customFieldOptions,
      reference: selectedCustomField.reference,
    };

    await axios
      .post("portal/attendances/custom_fields/edit.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function assignGroup() {
    if (selectedAgentGroup === null) {
      setSeverity("error");
      setErrMsg("An agent group is required.");
      setOpen(true);
      return;
    }

    setOpenNewAgentDialog(false);
    setShowLoading(true);
    const json = {
      reference: ref,
      group: selectedAgentGroup,
    };

    await axios
      .post("portal/attendances/agent_groups/assign.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function assignAggregator() {
    if (selectedAccountAggregator === null) {
      setSeverity("error");
      setErrMsg("An agent group is required.");
      setOpen(true);
      return;
    }

    setOpenNewAggregatorDialog(false);
    setShowLoading(true);
    const json = {
      reference: ref,
      aggregator: selectedAccountAggregator,
    };

    await axios
      .post("portal/attendances/aggregators/assign.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function changePackage() {
    if (selectedPackage === null) {
      setSeverity("error");
      setErrMsg("Plan is required.");
      setOpen(true);
      return;
    }

    setOpenPackagesDialog(false);
    setShowLoading(true);
    const json = {
      reference: ref,
      package: selectedPackage,
    };

    await axios
      .post("portal/attendances/renew.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openLocationDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          <MDBox>Add Location</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <MDInput
                name="title"
                margin="dense"
                label="Location Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setLocationTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <MDBox>
                <GoogleAddressField
                  displayName="Search Address On Google Maps"
                  setAddress={() => {}}
                  setLatitude={setLocationLat}
                  setLongitude={setLocationLng}
                />
              </MDBox>
            </MDBox>
            <MDBox mt={3}>
              <MDInput
                name="coverage_radius"
                margin="dense"
                label="Coverage Radius Around Location (in meters)"
                type="number"
                value={locationRad}
                fullWidth
                variant="outlined"
                onChange={(e) => setLocationRad(e.target.value)}
              />
            </MDBox>

            {centerLocation !== null && (
              <MDBox mt={5} mb={3}>
                <div style={{ height: "50vh", width: "100%" }}>
                  <MDBox>
                    <MapPicker
                      defaultLocation={centerLocation}
                      zoom={18}
                      mapTypeId="roadmap"
                      style={{ height: "50vh" }}
                      onChangeLocation={(lat, lng) => {
                        onLocationPicked(lat, lng);
                      }}
                      apiKey={AppConfig.google_map_key}
                    />
                  </MDBox>
                </div>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenLocationDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="contained"
            onClick={() => {
              createLocation();
            }}
          >
            Create
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openEditLocationDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          <MDBox>Edit Location</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <MDInput
                value={locationTitle}
                name="title"
                margin="dense"
                label="Location Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setLocationTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <MDBox>
                <GoogleAddressField
                  displayName="Search Address On Google Maps"
                  setAddress={() => {}}
                  setLatitude={setLocationLat}
                  setLongitude={setLocationLng}
                />
              </MDBox>
            </MDBox>
            <MDBox mt={3}>
              <MDInput
                name="coverage_radius"
                value={locationRad}
                margin="dense"
                label="Coverage Radius Around Location (in meters)"
                type="number"
                fullWidth
                variant="outlined"
                onChange={(e) => setLocationRad(e.target.value)}
              />
            </MDBox>

            {centerLocation !== null && (
              <MDBox mt={5} mb={3}>
                <div style={{ height: "50vh", width: "100%" }}>
                  <MDBox>
                    <MapPicker
                      defaultLocation={centerLocation}
                      zoom={18}
                      mapTypeId="roadmap"
                      style={{ height: "50vh" }}
                      onChangeLocation={(lat, lng) => {
                        onLocationPicked(lat, lng);
                      }}
                      apiKey={AppConfig.google_map_key}
                    />
                  </MDBox>
                </div>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenEditLocationDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="contained"
            onClick={() => {
              editLocation();
            }}
          >
            Update
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openNewFieldDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Add Custom Field</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mb={3}>
              <MDInput
                name="field_name"
                margin="dense"
                label="Field Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setCustomFieldTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                multiline
                minRows={3}
                placeholder="Comma separated values"
                name="field_options"
                margin="dense"
                label="Field Options (Optional)"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setCustomFieldOptions(e.target.value)}
              />
              <small>Comma separated values</small>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewFieldDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              createCustomField();
            }}
          >
            Add Field
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openEditFieldDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Edit Custom Field</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mb={3}>
              <MDInput
                name="field_name"
                margin="dense"
                label="Field Name"
                type="text"
                fullWidth
                variant="outlined"
                value={customFieldTitle}
                onChange={(e) => setCustomFieldTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                multiline
                minRows={3}
                placeholder="Comma separated values"
                name="field_options"
                margin="dense"
                label="Field Options (Optional)"
                type="text"
                fullWidth
                variant="outlined"
                value={customFieldOptions}
                onChange={(e) => setCustomFieldOptions(e.target.value)}
              />
              <small>Comma separated values</small>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenEditFieldDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              editCustomField();
            }}
          >
            Save Changes
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openNewAgentDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Assign Agent Group</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Agent Group</InputLabel>
                <Select
                  label="Agent Group"
                  onChange={(e) => {
                    setSelectedAgentGroup(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    agentGroups.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      agentGroups.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewAgentDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              assignGroup();
            }}
          >
            Assign
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openNewAggregatorDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Assign Supervisor</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Supervisor</InputLabel>
                <Select
                  label="Supervisor"
                  onChange={(e) => {
                    setSelectedAccountAggregator(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    accountAggregators.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      accountAggregators.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewAggregatorDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              assignAggregator();
            }}
          >
            Assign
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openPackagesDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Change Plan</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox mt={0} mb={3}>
            <MDBox mt={1}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Select Plan</InputLabel>
                <Select
                  label="Select Plan"
                  onChange={(e) => {
                    setSelectedPackage(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {packages.map((pckg) => (
                    <MenuItem value={pckg.reference}>{pckg.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenPackagesDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              changePackage();
            }}
          >
            Proceed
          </MDButton>
        </DialogActions>
      </Dialog>
      {pageLoaded && (
        <MDBox mb={5}>
          <MDBox mt={3} mb={1} display="flex" justifyContent="space-between">
            <MDBox>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  navigate(`/attendances`);
                }}
              >
                Back
              </Button>
            </MDBox>
            <MDBox>
              <Button
                color="dark"
                size="small"
                onClick={() => navigate(`/attendances/records/${ref}`)}
                startIcon={<Icon>list</Icon>}
              >
                Attendance Logs
              </Button>
            </MDBox>
          </MDBox>
          <MDBox mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4} lg={4}>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={1}>
                    {/* <MDBox mt={0}>
                      <MDTypography fontSize={28} align="center">
                        <Icon>label</Icon>
                      </MDTypography>
                    </MDBox> */}
                    <MDBox mt={1}>
                      <MDTypography variant="h6" align="center" fontWeight="light">
                        Plan
                      </MDTypography>
                      <MDTypography variant="h6" align="center" fontSize={18}>
                        {attendance.package}
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={2} align="center">
                      {/* <MDTypography variant="h6" align="center" fontWeight="light">
                        Checks
                      </MDTypography> */}
                      <MDBox position="relative" display="inline-flex">
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          size={120}
                          thickness={2}
                          sx={{ color: "#eee", position: "absolute" }}
                        />
                        <CircularProgress
                          variant="determinate"
                          value={percentageUsed}
                          size={120}
                          thickness={2}
                          color={getProgressColor()}
                        />

                        <MDBox
                          top={0}
                          left={0}
                          bottom={0}
                          right={0}
                          position="absolute"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <MDTypography variant="h6" align="center" fontSize={18}>
                            {attendance.quota - attendance.usedQuota}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      {/* <MDTypography variant="h6" align="center" fontSize={18}>
                        {attendance.quota}
                      </MDTypography> */}
                    </MDBox>
                    <MDBox mt={1}>
                      <MDTypography variant="h6" align="center" fontWeight="light">
                        Expires
                      </MDTypography>
                      <MDTypography variant="h6" align="center">
                        {new Date(parseInt(attendance.expires_on, 10) * 1000).toLocaleString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          }
                        )}
                      </MDTypography>
                    </MDBox>

                    <MDBox my={3} align="center">
                      <MDButton
                        color="dark"
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSelectedPackage(null);
                          setOpenPackagesDialog(true);
                        }}
                      >
                        Change Plan
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} md={8} lg={8}>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={3}>
                    <MDBox>
                      <MDTypography variant="h6">Attendance Tracker</MDTypography>
                    </MDBox>
                    <MDBox mt={3}>
                      <MDInput
                        value={attendanceTitle}
                        name="title"
                        margin="dense"
                        label="Tracker Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        onChange={(e) => setAttendanceTitle(e.target.value)}
                      />
                    </MDBox>
                    <MDBox mt={4}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4} lg={4}>
                          <MDBox mt={1}>
                            <FormControl fullWidth>
                              <InputLabel>Resumption Time</InputLabel>
                              <Select
                                label="Resumption Time"
                                onChange={(e) => {
                                  setCheckInTime(e.target.value);
                                }}
                                sx={{ padding: "12px", paddingLeft: "3px" }}
                                defaultValue={checkInTime}
                              >
                                {timeArray.map((row) => (
                                  <MenuItem value={row}>{row}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={4} lg={4}>
                          <MDBox>
                            <MDInput
                              name="attendance_grace_period"
                              margin="dense"
                              label="Grace Period (Minutes)"
                              type="number"
                              value={gracePeriod}
                              fullWidth
                              variant="outlined"
                              onChange={(e) => setGracePeriod(e.target.value)}
                            />
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} md={4} lg={4}>
                          <MDBox mt={1}>
                            <FormControl fullWidth>
                              <InputLabel>Closing Time (Optional)</InputLabel>
                              <Select
                                label="Closing Time (Optional)"
                                onChange={(e) => {
                                  setCheckOutTime(e.target.value);
                                }}
                                sx={{ padding: "12px", paddingLeft: "3px" }}
                                defaultValue={checkOutTime}
                              >
                                <MenuItem value={null}>---</MenuItem>
                                {timeArray.map((row) => (
                                  <MenuItem value={row}>{row}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </MDBox>
                        </Grid>
                      </Grid>
                    </MDBox>

                    <MDBox mt={3} display="flex" alignItems="center" ml={-1}>
                      <Checkbox
                        checked={enforceFaceMatch}
                        onChange={(e) => {
                          setEnforceFaceMatch(e.target.checked);
                        }}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                      <MDTypography
                        variant="button"
                        fontWeight="regular"
                        color="text"
                        sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
                      >
                        &nbsp;&nbsp;Enforce bio-metric face check?
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={5} align="right">
                      <MDButton
                        color="dark"
                        variant="contained"
                        onClick={() => {
                          editAttendance();
                        }}
                      >
                        Save Changes
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox mt={6}>
            {locations.length > 0 ? (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox>
                    <MDBox p={2}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDBox>
                          <MDTypography variant="h6" fontWeight="medium">
                            Locations
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="light" fontSize={14}>
                            Add attendance locations to enforce geo-fencing.
                          </MDTypography>
                        </MDBox>
                        <MDBox>
                          <MDButton
                            variant="contained"
                            color="dark"
                            iconOnly
                            onClick={() => {
                              setLocationTitle(null);
                              setLocationLat(null);
                              setLocationLng(null);
                              setLocationRad(30);
                              setOpenLocationDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                    <MDBox>
                      <DataTable
                        table={{ columns: locationsTableColumns, rows: locations }}
                        isSorted
                        entriesPerPage={false}
                        showTotalEntries
                        buttons
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            ) : (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={3}>
                    <MDBox align="center">
                      <MDBox>
                        <MDTypography variant="h6" fontSize={48}>
                          <Icon>place</Icon>
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                          Add physical locations where your teams are required to perform attendance
                          checks.
                          <br />
                          Once added, checks are geo-fenced to within the location.
                        </MDTypography>
                      </MDBox>
                      <MDBox mt={5} mb={3}>
                        <MDButton
                          variant="contained"
                          color="dark"
                          onClick={() => {
                            setLocationTitle(null);
                            setLocationLat(null);
                            setLocationLng(null);
                            setLocationRad(30);
                            setOpenLocationDialog(true);
                          }}
                          startIcon={<Icon>add</Icon>}
                        >
                          Add Location
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>

          <MDBox mt={5}>
            {groups.length > 0 ? (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox>
                    <MDBox p={3}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="h6" fontWeight="light">
                          <b>Teams</b>
                          <br />
                          <small>Assign agent groups to attendance tracker.</small>
                        </MDTypography>
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            setSelectedAgentGroup(null);
                            setOpenNewAgentDialog(true);
                          }}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDBox>
                      <DataTable
                        table={{ columns: groupsTableColumns, rows: groups }}
                        isSorted
                        entriesPerPage={false}
                        showTotalEntries
                        canSearch={false}
                        buttons
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            ) : (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={3}>
                    <MDBox align="center">
                      <MDBox>
                        <MDTypography variant="h6" fontSize={48}>
                          <Icon>groups</Icon>
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                          To track attendance, you must organise your teams in groups and assign
                          groups to this tracker.
                        </MDTypography>
                      </MDBox>
                      <MDBox mt={5} mb={3}>
                        <MDButton
                          variant="contained"
                          color="dark"
                          onClick={() => {
                            setSelectedAgentGroup(null);
                            setOpenNewAgentDialog(true);
                          }}
                          startIcon={<Icon>add</Icon>}
                        >
                          Assign Group
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>

          <MDBox mt={5}>
            {aggregators.length > 0 ? (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox>
                    <MDBox p={3}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="h6" fontWeight="light">
                          <b>Supervisors</b>
                          <br />
                          <small>Assign supervisors to attendance tracker.</small>
                        </MDTypography>
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            setSelectedAccountAggregator(null);
                            openNewAggregatorDialog(true);
                          }}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDBox>
                      <DataTable
                        table={{ columns: aggregatorsTableColumns, rows: aggregators }}
                        isSorted
                        entriesPerPage={false}
                        showTotalEntries
                        canSearch={false}
                        buttons
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            ) : (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={3}>
                    <MDBox align="center">
                      <MDBox>
                        <MDTypography variant="h6" fontSize={48}>
                          <Icon>groups</Icon>
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                          Track attendance of agents for each of your supervisors.
                        </MDTypography>
                      </MDBox>
                      <MDBox mt={5} mb={3}>
                        <MDButton
                          variant="contained"
                          color="dark"
                          onClick={() => {
                            setSelectedAgentGroup(null);
                            setOpenNewAggregatorDialog(true);
                          }}
                          startIcon={<Icon>add</Icon>}
                        >
                          Assign Supervisor
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>

          <MDBox mt={5}>
            {customFields.length > 0 ? (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox>
                    <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="medium">
                          Custom Fields
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light" fontSize={14}>
                          Capture required information during attendance checks
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <Tooltip title="Add Field">
                          <MDButton
                            variant="contained"
                            color="dark"
                            iconOnly
                            onClick={() => {
                              setCustomFieldTitle(null);
                              setCustomFieldOptions(null);
                              setOpenNewFieldDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                          </MDButton>
                        </Tooltip>
                      </MDBox>
                    </MDBox>
                    <MDBox mt={5}>
                      <DataTable
                        table={{ columns: customFieldTableColumns, rows: customFields }}
                        isSorted
                        canSearch={false}
                        entriesPerPage={false}
                        showTotalEntries
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            ) : (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={3}>
                    <MDBox align="center">
                      <MDBox>
                        <MDTypography variant="h6" fontSize={48}>
                          <Icon>edit_note</Icon>
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                          Need to capture information from your sales team during attendance checks?
                          Create a field.
                        </MDTypography>
                      </MDBox>
                      <MDBox mt={5} mb={3}>
                        <MDButton
                          variant="contained"
                          color="dark"
                          onClick={() => {
                            setCustomFieldTitle(null);
                            setCustomFieldOptions(null);
                            setOpenNewFieldDialog(true);
                          }}
                          startIcon={<Icon>add</Icon>}
                        >
                          Add Field
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}

export default AttendanceViewPage;
